import {
  Box,
  Checkbox,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { type ReactNode, useMemo, useState } from 'react';

import { JobTagIcon } from './job-tag-icon';
import { useJobTags } from './use-job-tags';

interface JobTagAddMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Tag ids currently on the job. */
  value: string[];
  /**
   * Called with the ONE tag that was clicked and whether it is now on. Not
   * the next id list: a list is built from whatever `value` held at the time,
   * so two quick clicks would each send a list without the other's tag.
   */
  onToggle: (tagId: string, selected: boolean) => void;
  /** The job's species — narrows the offered tags. */
  species: string | null | undefined;
}

/**
 * The tag picker that drops out of the job header's `+`.
 *
 * A menu rather than a dialog: putting one or two tags on a job is a small
 * act and shouldn't cost a modal, a Save and a Cancel. Each toggle writes
 * immediately, so there is nothing to confirm and nothing to lose by
 * dismissing.
 *
 * Toggling one id at a time — rather than replacing the list with whatever
 * the menu offers — is also what keeps a retired or wrong-species tag already
 * on the job from being silently stripped by an unrelated edit.
 * @param root0
 * @param root0.anchorEl
 * @param root0.onClose
 * @param root0.value
 * @param root0.onToggle
 * @param root0.species
 */
export function JobTagAddMenu({
  anchorEl,
  onClose,
  value,
  onToggle,
  species,
}: JobTagAddMenuProps) {
  const { tagsForSpecies } = useJobTags();
  const [search, setSearch] = useState('');

  const offered = useMemo(
    () => tagsForSpecies(species),
    [tagsForSpecies, species],
  );

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return offered;
    return offered.filter((t) => t.name.toLowerCase().includes(needle));
  }, [offered, search]);

  // Said in place of the list when there is nothing to list. Two different
  // nothings: no tag applies at all, or the search matched none of them.
  let emptyMessage: ReactNode = null;
  if (offered.length === 0) {
    emptyMessage = 'No tags apply to this species.';
  } else if (matches.length === 0) {
    emptyMessage = <>No tags match &ldquo;{search.trim()}&rdquo;.</>;
  }

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={() => {
        setSearch('');
        onClose();
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 260 } } }}
    >
      <Box sx={{ p: 1, pb: 0.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          placeholder="Search tags…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {emptyMessage ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ px: 2, py: 1.5 }}
        >
          {emptyMessage}
        </Typography>
      ) : (
        <MenuList dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {matches.map((tag) => {
            const selected = value.includes(tag._id);
            return (
              <MenuItem
                key={tag._id}
                onClick={() => onToggle(tag._id, !selected)}
              >
                <Checkbox
                  size="small"
                  edge="start"
                  disableRipple
                  checked={selected}
                  sx={{ p: 0.5, mr: 0.5 }}
                />
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <JobTagIcon iconKey={tag.icon} fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={tag.name}
                  primaryTypographyProps={{ noWrap: true }}
                />
              </MenuItem>
            );
          })}
        </MenuList>
      )}
    </Popover>
  );
}
